import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { serviceSchema } from "../seo/pageSchema";

const RothMultiTankServicePage = () => {
  const title = "Roth MultiTank Supplier & Septic Tank Installation — Thunder Bay | Groundhogs™";
  const description =
    "Authorized Roth MultiTank dealer and installer supplying lightweight poly septic tanks, holding tanks and potable water cisterns across Thunder Bay, Nipigon, Atikokan, Dryden and Greenstone. Buy tanks direct from Groundhogs or hire our crew to supply and install complete septic and water storage systems.";

  return (
    <div>
      <SEO
        title={title}
        description={description}
        structuredData={serviceSchema(title, description)}
        keywords="Roth MultiTank, Roth septic tanks, plastic septic tanks, poly septic tank installation, water tank supplier Thunder Bay, potable water storage tank for cottages"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="gh-intro gh-photo-intro pt-32 pb-16 relative"
        style={{
          backgroundImage:
            "url('https://www.roth-america.com/app/uploads/2020/08/RNA_MultiTank_1200-768x768.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(28, 43, 58, 0.85)" }}
        />
        <div className="gh-photo-intro-copy relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white" data-reveal>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{title}</h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto">{description}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-gray-700">
            <p className="text-lg">
              Groundhogs Septic Systems is an <strong>authorized Roth North America MultiTank dealer and installer</strong>
              {" "}
              serving Thunder Bay, Nipigon, Atikokan, Dryden, Greenstone and surrounding Northwestern Ontario
              communities. If you are searching for a <strong>septic tank supplier in Thunder Bay</strong>, a
              {" "}
              <strong>poly septic tank installation near me</strong>, or a
              {" "}
              <strong>potable water storage tank for your cottage</strong>, we can supply Roth MultiTank units and, if you
              wish, provide full supply-and-install service.
            </p>

            <h2
              className="text-3xl font-bold"
              style={{ color: "var(--dark-navy)" }}
            >
              What Is the Roth MultiTank?
            </h2>
            <p>
              The <strong>Roth MultiTank</strong> is a multi-purpose underground plastic tank that can be used as a
              {" "}
              <strong>septic tank</strong>, <strong>pump tank</strong>, <strong>holding tank</strong> or
              {" "}
              <strong>water cistern / rainwater tank</strong>. It is built with Roth&apos;s patented four-layer
              construction: an inner layer of FDA-approved virgin HDPE, two interior structural layers, and a black
              UV-stabilized outer shell. The tank is <strong>CSA, NSF and IAPMO certified</strong> (including
              {" "}
              <strong>NSF/ANSI 61 for potable water</strong>) and is designed to handle real-world Canadian conditions.
            </p>

            <div className="flex justify-center my-8">
              <img
                src="https://www.roth-america.com/app/uploads/2020/08/RNA_MultiTank_1200-768x768.jpg"
                alt="Roth MultiTank underground plastic septic and water tank"
                className="max-w-md w-full rounded-lg shadow-md"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3
                  className="text-xl font-bold mb-3"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Why Homeowners Choose Roth MultiTank
                </h3>
                <ul className="space-y-2">
                  <li>Lightweight plastic septic tank — much easier to move on rural and cottage properties.</li>
                  <li>
                    Low-profile design means <strong>less digging</strong> and better performance on high-water-table
                    sites.
                  </li>
                  <li>Engineered resin and cylindrical shape for extremely high strength under burial loads.</li>
                  <li>Multi-port inlets and outlets for flexible drain field and pump configurations.</li>
                  <li>Threaded riser system and large manways for safer access and service.</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3
                  className="text-xl font-bold mb-3"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Roth MultiTank vs. Concrete Tanks
                </h3>
                <ul className="space-y-2">
                  <li>
                    <strong>Lightweight vs. concrete</strong> — easier transport and placement on remote or wet sites.
                  </li>
                  <li>
                    <strong>Corrosion resistant</strong> plastic construction for long service life.
                  </li>
                  <li>No water required during backfill, saving time and trucking costs.</li>
                  <li>Factory-moulded tank with watertight threaded riser system.</li>
                  <li>Backed by Roth&apos;s <strong>industry-leading warranty</strong> for corrosion protection.</li>
                </ul>
              </div>
            </div>

            <h2
              className="text-3xl font-bold"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic Tank &amp; Holding Tank Applications
            </h2>
            <p>
              The Roth MultiTank is ideal as a <strong>plastic septic tank</strong> or
              {" "}
              <strong>septic holding tank</strong> for homes and cottages throughout Northwestern Ontario. Many properties
              in Thunder Bay and the surrounding districts are moving away from older, deteriorating concrete tanks to
              {" "}
              <strong>lightweight poly septic tanks</strong> that are easier to deliver and install.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Primary septic tanks</strong> for new septic system installations.
              </li>
              <li>
                <strong>Septic tank replacement</strong> when aging concrete tanks are cracked, leaking or undersized.
              </li>
              <li>
                <strong>Holding tanks</strong> for small or seasonal properties where a full leach field is not
                feasible.
              </li>
              <li>
                <strong>Pump tanks</strong> feeding raised beds, Eljen fields or distant leach fields.
              </li>
            </ul>

            <p>
              When you work with Groundhogs, our design team looks at your soil, water table, access and leach field
              layout to determine whether a Roth MultiTank is the best fit, or whether a traditional concrete tank
              remains the better choice for your site.
            </p>

            <h2
              className="text-3xl font-bold"
              style={{ color: "var(--dark-navy)" }}
            >
              Potable Water, Rainwater &amp; Cistern Use
            </h2>
            <p>
              Because the Roth MultiTank is manufactured with an inner layer of <strong>FDA-approved HDPE</strong> and
              certified to <strong>NSF/ANSI 61</strong>, it can also be configured as a
              {" "}
              <strong>potable water tank</strong> or <strong>rainwater cistern</strong>. For rural homes, cabins and
              cottages around Thunder Bay, Nipigon, Atikokan, Dryden and Greenstone, this makes it an excellent option
              for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Drinking water storage tanks</strong> tied into hauled-water deliveries.
              </li>
              <li>
                <strong>Rainwater collection cisterns</strong> for non-potable uses and irrigation.
              </li>
              <li>
                <strong>Underground water storage tanks</strong> where freezing is a concern.
              </li>
              <li>
                <strong>Agricultural water tanks</strong> supplying barns, livestock or remote taps.
              </li>
            </ul>
            <p>
              If you are specifically looking for a <strong>water tank supplier in Thunder Bay</strong> or a
              {" "}
              <strong>potable water storage tank for a cottage or camp</strong>, our team can size a Roth MultiTank for
              your daily and seasonal needs and coordinate all required plumbing and approvals.
            </p>

            <h2
              className="text-3xl font-bold"
              style={{ color: "var(--dark-navy)" }}
            >
              Buy Roth MultiTank Supply-Only or Supply &amp; Install
            </h2>
            <p>
              Groundhogs supplies Roth MultiTank units to <strong>homeowners, contractors, plumbers and septic
              installers</strong> throughout Northwestern Ontario. You can purchase tanks <strong>supply-only</strong> for
              your own installation, or hire our crew to provide <strong>full supply-and-install</strong> service as part
              of a complete septic or water storage project.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div
                className="bg-orange-50 border-l-4 p-6"
                style={{ borderColor: "var(--primary-orange)" }}
              >
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Supply-Only Roth Tank Sales
                </h3>
                <ul className="space-y-2">
                  <li>Order Roth MultiTank units for your own projects.</li>
                  <li>Ideal for licensed installers, contractors and builders.</li>
                  <li>Pickup in Thunder Bay or coordinated delivery to site.</li>
                </ul>
              </div>
              <div className="bg-gray-900 text-white p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">Supply &amp; Install Packages</h3>
                <ul className="space-y-2">
                  <li>Full septic tank installation or replacement using Roth MultiTank.</li>
                  <li>Complete potable water or cistern system design and installation.</li>
                  <li>Permits, excavation, backfill and final grading handled by our crew.</li>
                </ul>
              </div>
            </div>

            <h2
              className="text-3xl font-bold"
              style={{ color: "var(--dark-navy)" }}
            >
              Where We Supply &amp; Install Roth MultiTank
            </h2>
            <p>
              We provide <strong>Roth MultiTank sales and installation</strong> across Thunder Bay and Northwestern
              Ontario, including <strong>Thunder Bay, Nipigon, Atikokan, Dryden and Greenstone</strong> plus surrounding
              rural communities and lakefront townships.
            </p>

            <div
              className="mt-8 p-6 rounded-lg"
              style={{ backgroundColor: "var(--light-gray)" }}
            >
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: "var(--dark-navy)" }}
              >
                Get Pricing for Roth MultiTank in Thunder Bay
              </h3>
              <p className="mb-4">
                If you&apos;re comparing <strong>plastic septic tanks</strong>,
                {" "}
                <strong>poly septic tank installation</strong> or
                {" "}
                <strong>underground water storage tanks</strong> for a property in Northern Ontario, our team can help
                you decide whether Roth MultiTank is the right fit. We can provide supply-only pricing or a complete
                quote that includes excavation, installation and hookup to your septic field or water system.
              </p>
              <p className="mb-4">
                Call us at <strong>1-888-907-3777</strong> or request a quote using our contact form and mention
                {" "}
                <strong>Roth MultiTank</strong> so we can route your enquiry to the right team member.
              </p>
              <p className="mb-2">
                You can also learn more about the product directly from Roth North America here:
                {" "}
                <a
                  href="https://www.roth-america.com/product/septic-cistern-rainwater/"
                  className="text-orange-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Roth MultiTank product page
                </a>
                .
              </p>
              <p>
                For full septic system design and installation details, you can also visit our
                {" "}
                <Link
                  to="/services/septic-system-installation-thunder-bay"
                  className="text-orange-600 hover:underline"
                >
                  Septic System Installation &amp; Replacement
                </Link>{" "}
                page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RothMultiTankServicePage;
